import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Modal } from 'antd';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import config from '../../../config';
import CataloguePicker from '../../journalTransactions/CataloguePicker';
import useRefLists from '../../rapports/useRefLists';
import useMontantsEchange from '../../../hooks/useMontantsEchange';
import EchangeCommande from '../echangeCommande/EchangeCommande';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeLoading, NdoeError,
} from '../../../components/ndoe';

/*
  Choix de la paire de REMPLACEMENT d'un échange (/echange/:idCommande).

  Cet écran était une grille de filtres maison (famille, marque, cible, taille,
  couleur, matière) doublée d'une pagination : six appels de référentiel, des
  vignettes sans vérité de stock — on pouvait donc choisir une paire en RUPTURE
  et ne l'apprendre qu'au refus du serveur, une fois le wizard rempli.

  Il est remplacé par `CataloguePicker`, le sélecteur visuel déjà utilisé par le
  wizard de commande et quatre autres écrans : vignettes, recherche par
  référence, tri par stock disponible, prix derrière la permission, et surtout
  `bloquerRupture` — une paire indisponible n'est plus cliquable du tout.

  ⚠ Les paramètres d'URL (`id_detail`, `id_variante_origine`) ne sont PAS
  touchés : c'est `EchangeCommande` qui les lit pour faire dériver la paire
  d'origine par le serveur. Les perdre relancerait le bug de stock corrigé en
  api#208 (fail-closed).
*/

const EchangeCat = () => {
  const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const idCommande = pathname.split('/')[2];

  // Admin, ou permission retours.voir_montants — la règle vit dans
  // src/hooks/useMontantsEchange.js, un seul endroit pour tout le parcours.
  const peutVoirPrix = useMontantsEchange();
  const refs = useRefLists(true);

  const [commande, setCommande] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  // Ouvert d'emblée : on arrive ici POUR choisir une paire, pas pour regarder
  // un écran vide avec un bouton.
  const [pickerOuvert, setPickerOuvert] = useState(true);
  const [choix, setChoix] = useState(null);

  useEffect(() => {
    let vivant = true;
    setChargement(true); setErreur('');
    axios.get(`${DOMAIN}/api/commande/commandeOne/${idCommande}`)
      .then(({ data }) => { if (vivant) setCommande(Array.isArray(data) ? data[0] : data); })
      .catch((e) => { if (vivant) setErreur(e?.response?.data?.error || 'Commande introuvable.'); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [DOMAIN, idCommande]);

  const choisir = (variante) => {
    if (!variante?.id_varianteProduit) { toast.error('Paire invalide.'); return; }
    setChoix(variante);
    setPickerOuvert(false);
  };

  if (chargement) return <NdoePage><NdoeLoading rows={4} /></NdoePage>;
  if (erreur) return <NdoePage><NdoeError text={erreur} onRetry={() => navigate('/echangeForm')} /></NdoePage>;

  return (
    <NdoePage>
      <NdoePageHeader
        title="Choisir la paire de remplacement"
        subtitle={`Commande #${idCommande}${commande?.nom ? ` · ${commande.nom}` : ''}`}
        actions={(
          <NdoeActionButton variant="secondary" onClick={() => navigate('/echangeForm')}>
            Retour aux demandes
          </NdoeActionButton>
        )}
      />

      <div className="ndoe-card" style={{ padding: 16 }}>
        {choix ? (
          <>
            <p style={{ margin: 0 }}>
              Paire retenue : <b>{choix.nom_produit}</b>
              {choix.taille ? ` · P${choix.taille}` : ''}
              {choix.description ? ` · ${choix.description}` : ''}
            </p>
            <div style={{ marginTop: 12 }}>
              <NdoeActionButton variant="secondary" onClick={() => setPickerOuvert(true)}>
                Choisir une autre paire
              </NdoeActionButton>
            </div>
          </>
        ) : (
          <>
            <p style={{ margin: 0 }}>Aucune paire choisie pour le moment.</p>
            <div style={{ marginTop: 12 }}>
              <NdoeActionButton variant="primary" onClick={() => setPickerOuvert(true)}>
                Ouvrir le catalogue
              </NdoeActionButton>
            </div>
          </>
        )}
      </div>

      <CataloguePicker
        open={pickerOuvert}
        onClose={() => setPickerOuvert(false)}
        refLists={refs}
        avecPrix={peutVoirPrix}
        bloquerRupture
        onSelect={choisir}
      />

      {/* Le wizard d'échange reprend la main dès qu'une paire est retenue. */}
      <Modal
        centered
        open={!!choix}
        onCancel={() => setChoix(null)}
        width={950}
        footer={null}
        destroyOnClose
      >
        {choix && (
          <EchangeCommande
            key={`${choix.id_varianteProduit}-${search}`}
            idVariant={choix.id_varianteProduit}
            idCommande={idCommande}
          />
        )}
      </Modal>
    </NdoePage>
  );
};

export default EchangeCat;
